import React from 'react';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../context/ProductBuilder/machine';
import { getBaseProduct } from '../../../helpers/getBaseProduct';
import { getBaseProductType } from '../../../helpers/getBaseProductType';
import { getProductStateConfig } from '../../../helpers/getProductStateConfig';
import { isCurrentState } from '../../../helpers/isCurrentState';
import { getLining } from '../../../helpers/lining';
import { mapOptionsOrder } from '../../../helpers/optionsSort';
import { throwError } from '../../../helpers/throwError';
import {
    BaseProduct,
    ProductBuilderMachineEvent,
} from '../../../types';
import { Heading } from '../../Common/Heading';
import { Selector, SelectorImageItem } from '../../Common/Selector';
import { SectionContent } from './SectionContent';
import { CurtainHelpModal } from '../CurtainHelpModal';
import { HelpTooltip } from '../../Icon';
import styled from 'styled-components';

const StyledHelpIcon = styled.span`
  width: 42px;
  height: 42px;
  padding-left: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

type OnClick = (
    lining: string,
    colour: string,
    products: BaseProduct[],
    send: (event: ProductBuilderMachineEvent) => void,
    isCurtain: boolean
) => () => void;

const onClick: OnClick = (lining, colour, products, send, isCurtain) => () => {
    if (isCurtain) {
        // Curtain logic - reset lining colour related fields when changing lining type
        const customUpdate: any = {
            lining,
            liningColour: undefined, // Reset lining colour selection
            lining_colour_variant_id: undefined, // Reset variant ID
            lining_colour_price: 0, // Always reset price when changing lining type - user must select colour to add price
        };

        send({
            type: 'UPDATE_CUSTOM',
            custom: customUpdate,
        });
    } else {
        // Other products logic - simpler update
        send({
            type: "UPDATE_CUSTOM",
            custom: {
                lining,
            },
        });
    }
};

export const Lining = () => {
    const [state, send] = useService(productBuilderMachine);
    const {
        products,
        custom: { colour, lining },
    } = state.context;
    const productType = getBaseProductType(state.context);
    const [isHelpModalOpen, setIsHelpModalOpen] = React.useState(false);

    // Determine if this is a curtain or Roman Blinds
    const isCurtain = state.context.products[0]?.type === 'Curtain';
    const isRomanBlinds = state.context.products[0]?.type?.includes('Roman Blinds');

    // Get lining options based on product type
    let liningOptions: string[] = [];
    
    if (isCurtain) {
        // For curtains, use metafields from baseProduct
        const baseProduct = getBaseProduct(state.context);
        liningOptions = baseProduct ? baseProduct.metafields.lining || [] : [];
    } else if (isRomanBlinds) {
        // For Roman Blinds, get lining options from the first product's variants before any filtering
        // This ensures we get all available lining options regardless of current selections
        const firstProduct = state.context.products[0];
        if (firstProduct && firstProduct.options.includes('Lining')) {
            const liningKey = firstProduct.options.findIndex(option => option === 'Lining');
            const optionKey = `option${liningKey + 1}` as 'option1' | 'option2' | 'option3';
            
            liningOptions = Array.from(
                new Set(
                    firstProduct.variants.map(variant => variant[optionKey] as string).filter(Boolean)
                )
            );
        }
        
        // Apply sorting for Roman Blinds
        const liningTypesOrder = ["Blockout", "Light Filter", "Unlined"];
        liningOptions = mapOptionsOrder([...liningOptions], liningTypesOrder);
    } else {
        // For other products, use getLining helper with sorting
        const rawLiningOptions = getLining(state.context);
        const liningTypesOrder = ["Blockout", "Light Filter", "Unlined"];
        liningOptions = mapOptionsOrder([...rawLiningOptions], liningTypesOrder);
    }

    // Error handling
    if (isCurrentState("lining", state) && liningOptions.length === 0) {
        if (isCurtain) {
            const baseProduct = getBaseProduct(state.context);
            const debugInfo = {
                baseProduct: !!baseProduct,
                colour: state.context.custom.colour,
                productType: state.context.products[0]?.type,
                productCount: state.context.products.length,
                availableProducts: state.context.products.map(p => ({
                    title: p.title,
                    tags: p.tags.filter(tag => tag.includes('Colour:')).slice(0, 2)
                })).slice(0, 3)
            };
            
            throwError(state, `No lining options found in metafields. Debug: ${JSON.stringify(debugInfo)}`);
        } else if (isRomanBlinds) {
            const debugInfo = {
                productType: state.context.products[0]?.type,
                productCount: state.context.products.length,
                firstProductOptions: state.context.products[0]?.options,
                firstProductVariantsCount: state.context.products[0]?.variants?.length,
                colour: state.context.custom.colour
            };
            
            throwError(state, `No Roman Blinds lining options found in variants. Debug: ${JSON.stringify(debugInfo)}`);
        } else {
            throwError(state);
        }
    }

    const openHelpModal = () => {
        if (isCurtain) setIsHelpModalOpen(true);
    };

    const closeHelpModal = () => {
        setIsHelpModalOpen(false);
    };

    return (
        <SectionContent>
            <StyledHeadingWrapper>
                <Heading>Lining</Heading>
                {isCurtain && (
                    <StyledHelpIcon onClick={openHelpModal}>
                        <HelpTooltip />
                    </StyledHelpIcon>
                )}
            </StyledHeadingWrapper>
            <Selector isSlider={true}>
                {liningOptions.map(option => {
                    const { showIcon, Icon, description } = getProductStateConfig(
                        productType,
                        'lining',
                        option,
                    );
                    
                    // Handle selection logic differently for curtains vs Roman Blinds vs other products
                    const isSelected = isCurtain 
                        ? option === (typeof lining !== 'undefined' ? lining.split(':')[0] : '')
                        : option === lining;
                    
                    return (
                        <SelectorImageItem
                            isSelected={isSelected}
                            onClick={onClick(option, colour, products, send, isCurtain)}
                            showIcon={showIcon}
                            Icon={Icon}
                            title={option}
                            key={option}
                            description={description}
                        />
                    );
                })}
            </Selector>
            {isCurtain && (
                <CurtainHelpModal
                    modalIsOpen={isHelpModalOpen}
                    closeModal={closeHelpModal}
                    stepType="lining"
                />
            )}
        </SectionContent>
    );
};
